import { Injectable } from '@angular/core';
import {
  AgencyDefaultShiftGQL,
  CreateUserGQL,
  CreateUserMutationVariables,
  SaveUserProfileGQL,
  SaveUserProfileMutationVariables,
  ShiftNode,
  UpdateUserGQL,
  UpdateUserMutationVariables,
  UserShiftsGQL,
} from '@generated/graphql';
import { catchError, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { CognitoAuthService } from '@auth/services/cognito-auth.service';
import { GraphqlID } from '@app/shared/types';
import {
  toUserWriteResult,
  UserWriteResult,
} from '@app/pages/users/forms/user-form/user-shift-refusal';
import { DropdownItem } from '@voyagu/radar-design-system';
import { mapUserToDropdown } from '@app/pages/users/forms/user-form/user-form.component';
import { AccessibleAgentsService } from '@app/shared/services/accessible-agents/accessible-agents.service';
import { RefetchQueries } from '@app/shared/types';

/**
 * TIC-7021: both write doors can refuse with `USER_SHIFT_REQUIRED`, which the two user forms say in
 * their own words, so the global error UI is suppressed per operation the way `AgentDateService` and
 * `ShiftsPageService` do it. `errorPolicy: 'all'` keeps the errors on the result.
 */
const FORM_SURFACED_ERRORS = {
  errorPolicy: 'all' as const,
  context: { skipGraphqlErrorUi: true },
};

/** The agency default as the two user pages hold it — `null` where the agency set none. */
export type AgencyDefaultShift = { defaultShiftId: GraphqlID | null };

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  constructor(
    private updateUserGQL: UpdateUserGQL,
    private createUserGQL: CreateUserGQL,
    private accessibleAgentsService: AccessibleAgentsService,
    private saveUserProfileGQL: SaveUserProfileGQL,
    private userShiftsGQL: UserShiftsGQL,
    private agencyDefaultShiftGQL: AgencyDefaultShiftGQL,
    private cognitoAuthService: CognitoAuthService,
  ) {}

  updateUser(
    data: UpdateUserMutationVariables,
    refetchQueries: RefetchQueries = [],
  ): Observable<UserWriteResult> {
    return this.updateUserGQL
      .mutate(data, { refetchQueries, ...FORM_SURFACED_ERRORS })
      .pipe(map((resp) => toUserWriteResult(resp.errors, resp.data?.updateUser?.ok)));
  }

  createUser(
    data: CreateUserMutationVariables,
    refetchQueries: RefetchQueries = [],
  ): Observable<UserWriteResult & { userId?: GraphqlID }> {
    return this.createUserGQL.mutate(data, { refetchQueries, ...FORM_SURFACED_ERRORS }).pipe(
      map((resp) => ({
        ...toUserWriteResult(resp.errors, resp.data?.createUser?.ok),
        userId: resp.data?.createUser?.result?.id,
      })),
    );
  }

  /**
   * TIC-7021: the shift in force for the caller's own agency, by id.
   *
   * Through `user(id:)` with the caller's own id (the precedent `ClientsService` reads it from),
   * because `userAuth.agency` is `CurrentUserAgencyNode` and is deliberately narrow. A failed read
   * answers no default rather than withholding the create form: the global middleware has already
   * reported it, and a form without a preselect is still a usable form.
   */
  agencyDefaultShiftId(): Observable<AgencyDefaultShift> {
    const id = this.cognitoAuthService.user.id;

    return this.agencyDefaultShiftGQL.fetch({ id }).pipe(
      map(({ data }) => ({
        defaultShiftId: (data?.user?.agency?.defaultShift?.id as GraphqlID) ?? null,
      })),
      catchError(() => of({ defaultShiftId: null })),
    );
  }

  getAccessibleAgentsDropdownItems(): Observable<DropdownItem[]> {
    return this.accessibleAgentsService
      .getAccessibleAgents()
      .pipe(map((agents) => agents.map((el) => ({ ...mapUserToDropdown(el) }))));
  }

  saveUserProfile(
    variables: SaveUserProfileMutationVariables,
    refetchQueries: RefetchQueries = [],
  ) {
    return this.saveUserProfileGQL.mutate(variables, { refetchQueries });
  }

  userShifts() {
    return this.userShiftsGQL
      .fetch()
      .pipe(map(({ data }) => (data.userShifts || []) as ShiftNode[]));
  }
}
